f<template>
    <seismic-layout>
        <main class="summary-page">
            <section class="header">
                <h2>5. Summary of Brace Configurations</h2>
            </section>
            <section>
                <form class="generate-report" action="/actions/anvil-module/seismic-brace-configuration/create-report" method="get">
                    <div class="summary">
                        <div class="header">
                            <h3>Sub-project name: <strong v-text="record.name"></strong></h3>
                            <router-link :to="{ name: 'seismic-setup', params: { id: record.id }}"><a class="edit-project"><i class="fa fa-pencil-square-o"></i> Edit Project</a></router-link>
                        </div>

                        <fieldset>
                            <div class="row-header">
                                <div class="order">
                                    Drag Order
                                </div>
                                <div>
                                    Brace Name
                                </div>
                                <div>
                                    Drawing Reference
                                </div>
                                <div class="qty">
                                    Quantity
                                </div>
                                <div>
                                    Seismic Load
                                </div>
                                <div>
                                    Structure
                                </div>
                                <div>
                                    Brace Description
                                </div>
                                <div>
                                    Fastener
                                </div>
                                <div>
                                    Attachments
                                </div>
                                <div class="edit">
                                </div>
                                <div class="check">
                                    Include in Report
                                    (&nbsp;<a @click.prevent="selectBrace(true)" href="#">Select&nbsp;All</a>&nbsp;)
                                </div>
                            </div>
                        </fieldset>

                        <loading v-if="loading"></loading>

                        <draggable v-model="braces" :options="{ handle: '.order' }" @change="reorder">
                            <fieldset v-for="(brace, index) in braces">
                                <div>
                                    <div class="order ordering-handle" v-text="index + 1"></div>
                                    <div v-text="brace.name"></div>
                                    <div v-text="brace.drawingReference"></div>
                                    <div class="qty" v-text="brace.quantity"></div>
                                    <div v-if="brace.zoiSystemDesignLoad" v-text="lbOrKg(useMetric(1) ? brace.zoiSystemDesignLoad : parseInt(brace.zoiSystemDesignLoad), true, true)"></div>
                                    <div v-else>--</div>
                                    <div v-html="formatStructure(brace)" :class="{ 'user-defined': brace.udSubstrate }"></div>
                                    <div v-html="formatDescription(brace)"></div>
                                    <div v-html="formatFastener(brace)" :class="{ 'user-defined': brace.udFastener }"></div>
                                    <div v-if="brace.primaryStructuralAttachment || brace.primaryPipeAttachment">
                                        <modal :index="index" v-if="brace.primaryStructuralAttachment && brace.primaryStructuralAttachment.structuralAttachment" :result="brace.primaryStructuralAttachment.structuralAttachmentSubmittal" :text="brace.primaryStructuralAttachment.structuralAttachment" />
                                        <modal :index="index" v-if="brace.primaryStructuralAttachment" :result="brace.primaryStructuralAttachment.swivelSubmittal" :text="formatStructuralAttachment(brace.primaryStructuralAttachment)" />
                                        <modal :index="index" v-if="brace.primaryPipeAttachment" :result="brace.primaryPipeAttachment.pipeAttachmentSubmittal" :text="formatPipeAttachment(brace.primaryPipeAttachment)" />
                                    </div>
                                    <div v-else>--</div>
                                    <div class="edit" >
                                        <router-link :to="{ name: 'seismic-brace-identify', params: { seismicDesignId: id, id: brace.id }}">
                                            <span data-balloon="Edit" data-balloon-pos="up">
                                                <i class="fa fa-pencil" aria-hidden="true"></i>
                                            </span>
                                        </router-link>

                                        <summary-delete :title="brace.name" :id="brace.id"></summary-delete>
                                    </div>
                                    <div class="check">
                                        <input v-if="!getBraceStatus(brace)" type="checkbox" :id="checkboxId(brace)" name="braceIds[]" :value="brace.id" @click="selectBrace(false)" />
                                        <label v-if="!getBraceStatus(brace)" class="checkbox" :for="checkboxId(brace)"></label>
                                        <span class="warn inline-popup" :data-mfp-src="'#' + brace.id" v-else :title="getBraceStatus(brace)"></span>
                                        <div v-if="getBraceStatus(brace)" class="inline-modal mfp-hide" :id="brace.id">
                                            <div>
                                                <p>This brace's status is <strong>{{ getBraceStatus(brace) }}</strong>.</p>
                                                <p>
                                                    You can go back and
                                                        <router-link :to="{ name: 'seismic-brace-identify', params: { seismicDesignId: id, id: brace.id }}" class="edit">
                                                            edit
                                                        </router-link>
                                                    this brace to finalize and add it to your report.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </fieldset>
                        </draggable>

                        <fieldset>
                            <div>
                                <router-link :to="{ name: 'seismic-brace-new', params: { seismicDesignId: id }}" class="add-segment">
                                    <i class="fa fa-plus"></i> Add a new Brace Configuration
                                </router-link>
                                
                                <a v-if="hasBraces" :href="billOfMaterialsUrl" v-on:click.prevent="exportBillOfMaterials" class="add-segment">
                                    <i class="fa fa-download"></i> Export Bill of Materials
                                </a>
                            </div>
                        </fieldset>

                        <div v-if="hasUserDefined" class="user-defined note">
                            <span class="footnote">&sup1;</span> This field entry has been defined by the end user. Anvil International has not verified it's load capacity for this application.
                        </div>
                    </div>

                    <div class="summary uploaded-files" v-if="recordLoaded">
                        <div class="header">
                            <h3>User Uploaded Files</h3>
                        </div>
                        <div class="info">
                            Upload custom calculation and submittal PDF files that will be attached to the created report.
                        </div>

                        <div v-if="hasUploadedFiles">
                            <fieldset>
                                <div class="row-header">
                                    <div>
                                        Title
                                    </div>
                                    <div>
                                        Filename
                                    </div>
                                    <div class="edit">
                                        Delete
                                    </div>
                                </div>
                            </fieldset>

                            <fieldset v-for="(userFile, index) in record.userUploadedPDFs">
                                <div>
                                    <div v-text="userFile.title"></div>
                                    <div><a :href="userFile.url" v-text="userFile.filename" _target="blank"></a></div>
                                    <div class="edit">
                                        <file-delete :title="userFile.title" :id="userFile.id" :designId="id"></file-delete>
                                    </div>
                                </div>
                            </fieldset>
                        </div>

                        <fieldset>
                            <div>
                                <router-link :to="{ name: 'seismic-file-upload-new', params: { seismicDesignId: id }}" class="add-segment">
                                    <i class="fa fa-plus"></i> Upload Fastener Calculations and Submittal PDFs
                                </router-link>
                            </div>
                        </fieldset>
                    </div>

                    <div class="action">
                        <div>
                            <a href="/submittal-manager" class="button back simple alternate">Projects</a>
                        </div>
                        <div class="reports">
                            <button :class="{ disabled: !areBracesSelected }" type="submit" @click.prevent="createReport('report')"><span><i v-if="loadingDownload && reportType == 'report'" class="fa fa-refresh fa-spin fa-fw"></i> Create Report</span></button>
                            <button :class="{ disabled: !areBracesSelected }" type="submit" @click.prevent="createReport('submittalReport')"><span><i v-if="loadingDownload && reportType == 'submittalReport'" class="fa fa-refresh fa-spin fa-fw"></i> Report + Submittals</span></button>
                            <button :class="{ disabled: !areBracesSelected }" type="submit" @click.prevent="createReport('dwgReport')"><span><i v-if="loadingDownload && reportType == 'dwgReport'" class="fa fa-refresh fa-spin fa-fw"></i> Report + Submittals + DWG</span></button>
                            <div class="messaging warning" v-if="!areBracesSelected">No braces selected, select at least one brace to create report.</div>
                            <p class="note">Note: Reports can take several seconds to generate.</p>
                        </div>
                    </div>
                    <input type="hidden" name="designId" :value="this.$options.propsData.id" />
                    <input type="hidden" name="reportType" :value="reportType" />
                </form>
            </section>
        </main>
    </seismic-layout>
</template>

<script>
import SeismicLayout from './layout';
import SeismicHeader from './header';
import { mapState } from 'vuex';
import SummaryDelete from './summary-delete';
import FileDelete from './file-delete';

import axios from 'axios';
import Modal from './_modal';
import Draggable from 'vuedraggable'
import ClockvineMixin from 'vue/common/clockvine-mixin';
import MagnificPopup from 'magnific-popup';
import Cookies from 'js-cookie';

export default {
    components: {
        SeismicLayout,
        SeismicHeader,
        Draggable,
        Modal,
        SummaryDelete,
        FileDelete,
    },

    mixins: [
        ClockvineMixin,
    ],

    data: () => ({
        type: 'seismicDesign',
        braces: [],
        loading: false,
        reportType: '',
        selectedBraces: [],
        loadingDownload: false,
        hasUserDefined: false,
        recordLoaded: false,
    }),

    watch: {
        record() {
            this.recordLoaded = true;
        }
    },

    mounted() {
        this.loading = true;
        this.$store.dispatch('seismicDesignBraceConfiguration/reindex', { seismicDesignId: this.$options.propsData.id })
            .then(r => {
                this.loading = false;
                this.braces = this.$store.state.seismicDesignBraceConfiguration.indexes[`/actions/anvil-module/seismic-design-brace-configuration/index?seismicDesignId=${this.id}`];

                this.braces.forEach(b => {
                    b.primaryStructuralAttachment = b.structuralAttachmentOptions.find(a => a.primary === true);
                    b.primaryPipeAttachment = b.pipeAttachmentOptions.find(a => a.primary === true);
                });
                this.$nextTick(() => {
                    var $popup = $('.inline-popup');
                    if ($popup.length) {
                        $popup.magnificPopup({
                            type:'inline',
                            closeOnContentClick: true
                        });
                    }
                });
            });

            var $popup = $('.inline-popup');
            if ($popup.length) {
                $popup.magnificPopup({
                    type:'inline',
                    closeOnContentClick: true
                });
            }
    },

    methods: {
        reorder() {
            let url = `/actions/anvil-module/seismic-design-brace-configuration/reorder?seismicDesignId=${this.id}`;

            return axios.post(url, {
                braces: this.braces.map(b => b.id)
            });
        },

        formatStructuralAttachment(attachment) {
            if (!attachment) return '';
            return `${attachment.swivelConnection} ${attachment.swivelSize}`;
        },
        formatPipeAttachment(attachment) {
            if (!attachment) return '';
            return `${attachment.pipeAttachment} ${attachment.pipeAttachmentSize}`;
        },
        formatSeismicLoad(load) {
            return load ? `${load} lbs` : '--';
        },
        formatStructure(brace) {
            if (!brace.substrate) {
                return '--';
            } else if (this.isUserDefined(brace.substrate) && brace.udSubstrate) {
                this.hasUserDefined = true;
                return brace.udSubstrate + ' <span class="footnote">&sup1;</span>';
            }

            return brace.substrate;
        },
        formatDescription(brace) {
            if (!brace.orientation) {
                return '--';
            }

            let description = `${brace.orientation.toUpperCase()} ORIENTATION`;

            if (brace.calculatedFastenerOrientation && brace.calculatedFastenerOrientation != '[]') {
                description += `<br>${brace.calculatedFastenerOrientation}`;
            }

            if (brace.angleMin && brace.angleMax) {
                description += `<br>${brace.angleMin}° - ${brace.angleMax}°`;
            }

            return description;
        },
        formatFastener(brace) {
            if (!brace.fastener) {
                return '--';
            } else if (this.isUserDefined(brace.fastener) && brace.udFastener) {
                this.hasUserDefined = true;
                return brace.udFastener + ' <span class="footnote">&sup1;</span>';
            }

            return brace.fastener;
        },
        createReport(reportType) {
            let braceIds = this.selectedBraces;
            let form = document.querySelector('form.generate-report');

            if (this.loadingDownload) {
                return;
            }

            if (braceIds.length) {
                this.reportType = reportType;
                this.$nextTick(() => {
                    this.loadingDownload = true;

                    Cookies.set('downloadStarted', 0, { expires: 1 });
                    setTimeout(this.checkDownloadCookie, 500);

                    form.submit();
                });
            }
        },
        checkboxId(brace) {
            return 'brace-' + brace.id;
        },
        getBraceStatus(brace) {
            if (!brace.primaryPipeAttachment || !brace.primaryStructuralAttachment) {
                return 'Incomplete';
            }

            if (Boolean(brace.needsReview)) {
                return 'Needs Review';
            }

            return false;
        },
        selectBrace(all = false) {
            if (all) {
                let braces = document.querySelectorAll('.summary input[type=checkbox]');
                Array.prototype.forEach.call(braces, function(el, i){
                    el.checked = true;
                });
            }
            let selectedBraceIds = document.querySelectorAll('.summary input:checked');

            this.selectedBraces = selectedBraceIds;
        },
        checkDownloadCookie() {
            if (Cookies.get('downloadStarted') == 1) {
                Cookies.set('downloadStarted', 0, { expires: 1 });
                this.loadingDownload = false;
            } else {
                setTimeout(this.checkDownloadCookie, 500);
            }
        },
        isUserDefined(param) {
            if (param != 'undefined') {
                return param.toUpperCase() == 'USER DEFINED';
            }
            return false;
        },
        exportBillOfMaterials() {
            window.open(this.billOfMaterialsUrl);
        },
    },

    computed: {
        // useMetric() {
        //     return this.record.useMetric == 1;
        // },
        hasBraces() {
            return this.braces.length > 0;
        },
        areBracesSelected() {
            return this.selectedBraces.length > 0;
        },
        hasUploadedFiles() {
            if (!this.recordLoaded) {
                return false;
            }
            if (!this.record || !this.record.userUploadedPDFs) {
                return false;
            }

            return this.record.userUploadedPDFs.length;
        },
        billOfMaterialsUrl() {
            return '/submittal-manager/bill-of-materials/v1/' + this.id;
        },
    },
}
</script>
